/**
 * Componente: Resumen General
 * Ruta: /resumen-general
 * 
 * Dashboard consolidado que muestra:
 * - Estadísticas de licencias (totales, usadas, disponibles)
 * - Estadísticas de candidatos (evaluados, en progreso, descartados)
 * - Gráfico de pastel: distribución de candidatos por estado
 * - Gráfico de barras: distribución por nivel de ajuste al cargo
 * - Barra de progreso de uso de licencias
 * - Alertas cuando quedan pocas licencias (≤ 5)
 * - Botones de acciones rápidas (comprar, candidatos, informes, historial)
 * 
 * 📡 Integración Backend:
 * Este componente consume datos del servicio licenseService.js
 * 
 * ⚠️ Estado actual: Usando datos MOCK para desarrollo
 * ✅ Para producción: Ver comentarios marcados con "PRODUCCIÓN" en el código
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";
import { Card, Grid, Icon, CircularProgress } from "@mui/material";
import CandidatesPieChart from "components/Charts/CandidatesPieChart";
import JobFitBarChart from "components/Charts/JobFitBarChart";
import LicenseAlertCard from "components/LicenseAlertCard";
import StatCard from "components/StatCard";
import {
  getDashboardData,
  calculateLicenseUsagePercentage,
  getLicenseUsageColor,
  formatDate,
  shouldShowLicenseAlert,
} from "services/licenseService";

function ResumenGeneral() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Cargar datos del dashboard
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDashboardData();
      setDashboardData(data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error al cargar datos del dashboard:', err);
      // ⚠️ DESARROLLO: Muestra mensaje y vuelve a cargar datos mock
      setError('No se pudieron cargar los datos. Mostrando información de ejemplo.');
      const data = await getDashboardData();
      setDashboardData(data);
      // ✅ PRODUCCIÓN: Reemplazar las 3 líneas anteriores con solo esto:
      // setError('No se pudieron cargar los datos. Por favor, intenta nuevamente.');
      // (El usuario podrá hacer clic en "Actualizar datos" para reintentar)
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar datos al montar
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Handlers de navegación
  const handleBuyLicenses = useCallback(() => {
    navigate('/gestion-licencias/comprar');
  }, [navigate]);

  const handleNewCandidate = useCallback(() => {
    navigate('/gestion-candidato/cargar-candidatos');
  }, [navigate]);

  const handleViewHistory = useCallback(() => {
    navigate('/gestion-licencias/historial');
  }, [navigate]);

  const handleViewInformes = useCallback(() => {
    navigate('/informe-test');
  }, [navigate]);

  // Cálculos memoizados
  const { usagePercentage, progressColor, showAlert } = useMemo(() => {
    if (!dashboardData?.licenses) {
      return { usagePercentage: 0, progressColor: 'info', showAlert: false };
    }

    const { usedLicenses, totalLicenses, availableLicenses } = dashboardData.licenses;
    
    return {
      usagePercentage: calculateLicenseUsagePercentage(usedLicenses, totalLicenses),
      progressColor: getLicenseUsageColor(
        calculateLicenseUsagePercentage(usedLicenses, totalLicenses)
      ),
      showAlert: shouldShowLicenseAlert(availableLicenses),
    };
  }, [dashboardData]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!dashboardData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDTypography variant="h5" color="error">
            Error al cargar los datos del dashboard
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const { licenses, candidates, jobFit } = dashboardData;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Encabezado */}
        <MDBox mb={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <MDTypography variant="h4" fontWeight="bold">
                Resumen General
              </MDTypography>
              <MDTypography variant="body2" color="text" mt={1}>
                Vista consolidada del estado de tus licencias y candidatos
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={4} textAlign={{ xs: 'left', md: 'right' }}>
              <MDButton
                variant="gradient"
                color="info"
                startIcon={<Icon>refresh</Icon>}
                onClick={loadDashboardData}
              >
                Actualizar datos
              </MDButton>
            </Grid>
          </Grid>
          {lastUpdate && (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              Última actualización: {lastUpdate.toLocaleTimeString('es-ES')}
            </MDTypography>
          )}
          {error && (
            <MDBox mt={2} p={2} bgColor="warning" borderRadius="md">
              <MDTypography variant="caption" color="white">
                ⚠️ {error}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>

        {/* Alerta de licencias bajas */}
        {showAlert && (
          <MDBox mb={3}>
            <LicenseAlertCard availableLicenses={licenses.availableLicenses} />
          </MDBox>
        )}

        {/* Tarjetas estadísticas principales */}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Licencias Totales"
                value={licenses.totalLicenses}
                subtitle={`Plan: ${licenses.planType}`}
                icon="workspace_premium"
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Licencias Usadas"
                value={licenses.usedLicenses}
                subtitle={`${usagePercentage}% en uso`}
                icon="assignment_turned_in"
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Licencias Disponibles"
                value={licenses.availableLicenses}
                subtitle={showAlert ? '⚠️ Nivel crítico' : 'Disponibles'}
                icon="inventory"
                color={licenses.availableLicenses <= 5 ? 'warning' : 'success'}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Total Candidatos"
                value={candidates.total}
                subtitle="Registrados en el sistema"
                icon="people"
                color="dark"
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Barra de progreso de uso de licencias */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={3}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Uso de Licencias
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {licenses.usedLicenses} de {licenses.totalLicenses} licencias en uso
                </MDTypography>
              </MDBox>
              <MDProgress
                value={usagePercentage}
                color={progressColor}
                variant="gradient"
                label={true}
              />
              <MDBox mt={2} display="flex" justifyContent="space-between">
                <MDTypography variant="caption" color="text">
                  Fecha de expiración: {formatDate(licenses.expirationDate)}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Última compra: {formatDate(licenses.lastPurchaseDate)}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        {/* Gráficos */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <CandidatesPieChart
              data={candidates}
              title="Estado de Candidatos"
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <JobFitBarChart
              data={jobFit}
              title="Nivel de Ajuste al Cargo"
            />
          </Grid>
        </Grid>

        {/* Acciones rápidas */}
        <MDBox mt={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Acciones Rápidas
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    fullWidth
                    startIcon={<Icon>shopping_cart</Icon>}
                    onClick={handleBuyLicenses}
                  >
                    Comprar Licencias
                  </MDButton>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    startIcon={<Icon>person_add</Icon>}
                    onClick={handleNewCandidate}
                  >
                    Nuevo Candidato
                  </MDButton>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    fullWidth
                    startIcon={<Icon>assessment</Icon>}
                    onClick={handleViewInformes}
                  >
                    Ver Informes
                  </MDButton>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    startIcon={<Icon>history</Icon>}
                    onClick={handleViewHistory}
                  >
                    Historial
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ResumenGeneral;
